/**
 * Login-Banner: Immobilien-Schulden-Abrechnung.
 *
 * Erscheint einmal pro Browser-Session wenn ein Betrieb Schulden hat (status != ACTIVE).
 * Mobile: Vollbild-Modal. Desktop: zentriertes Overlay.
 * Prompt-Spec: Section 11.3
 */

import { api } from '../services/api.js';
import { gameState } from '../state/gameState.js';
import { navigate } from '../router.js';

const SESSION_KEY = '_nt_laundryBannerSeen';

interface DebtSummaryItem {
  businessId: string;
  businessName: string;
  status: 'ACTIVE' | 'CREDIT' | 'FROZEN';
  maintenanceDebtCents: string;
  maintenanceDebtDisplay: string;
  daysInDebt: number;
  hoursUntilFreeze: number | null;
  canPayWithBlackMoney: boolean;
  eurAlternativeCents: string;
}

function fmtSeur(cents: string): string {
  const val = Math.round(parseInt(cents, 10) / 100);
  return new Intl.NumberFormat('de-DE').format(val) + ' S-₦';
}

function closeBanner(): void {
  document.getElementById('laundry-debt-overlay')?.remove();
  sessionStorage.setItem(SESSION_KEY, '1');
}

function buildBannerHTML(items: DebtSummaryItem[]): string {
  const totalCents = items.reduce(
    (sum, d) => sum + parseInt(d.maintenanceDebtCents, 10),
    0,
  );

  const rows = items
    .map(
      (d) => `
      <div style="margin-bottom:var(--space-3);padding-bottom:var(--space-3);border-bottom:1px solid var(--border);">
        <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:4px;">
          <span style="font-family:var(--font-mono);font-size:12px;color:var(--text-primary);letter-spacing:0.05em;">${d.businessName}</span>
          <span style="font-family:var(--font-mono);font-size:10px;color:${d.status === 'FROZEN' ? 'var(--accent-red)' : 'var(--accent-orange)'};letter-spacing:0.08em;">
            ${d.status === 'FROZEN' ? 'EINGEFROREN' : 'AUF KREDIT'}
          </span>
        </div>
        <p style="font-family:var(--font-mono);font-size:11px;color:var(--text-muted);margin:0 0 2px 0;">
          Schulden: <span style="color:var(--accent-red);">${d.maintenanceDebtDisplay}</span>
        </p>
        ${
          d.hoursUntilFreeze !== null
            ? `<p style="font-family:var(--font-mono);font-size:11px;color:var(--text-muted);margin:0;">Einfrieren in: ${String(d.hoursUntilFreeze)}h</p>`
            : `<p style="font-family:var(--font-mono);font-size:10px;color:var(--text-muted);margin:0;">(Maximale Schulden erreicht – wächst nicht mehr)</p>`
        }
      </div>`,
    )
    .join('');

  return `
    <div class="modal-header">
      <h2 style="color:var(--accent-red);">IMMOBILIEN-ABRECHNUNG</h2>
      <p style="font-family:var(--font-mono);font-size:11px;color:var(--text-muted);margin:0;letter-spacing:0.05em;">
        Seit deinem letzten Login:
      </p>
    </div>
    <div class="modal-body">
      <div>${rows}</div>

      <div style="border-top:1px solid var(--border);padding-top:var(--space-3);">
        <div style="display:flex;justify-content:space-between;align-items:baseline;">
          <span style="font-family:var(--font-mono);font-size:11px;color:var(--text-muted);">Gesamt offen:</span>
          <span style="font-family:var(--font-mono);font-size:13px;color:var(--accent-red);letter-spacing:0.05em;">${fmtSeur(String(totalCents))}</span>
        </div>
      </div>
    </div>
    <div class="modal-actions" style="flex-wrap:wrap;">
      <button
        id="debt-banner-pay-btn"
        style="
          flex:1;min-width:140px;min-height:44px;
          font-family:var(--font-mono);font-size:11px;letter-spacing:0.08em;text-transform:uppercase;
          background:transparent;border:1px solid var(--accent-green);color:var(--accent-green);
          cursor:pointer;border-radius:0;padding:var(--space-2);
        "
      >JETZT BEZAHLEN</button>
      <button
        id="debt-banner-later-btn"
        style="
          flex:1;min-width:140px;min-height:44px;
          font-family:var(--font-mono);font-size:11px;letter-spacing:0.08em;text-transform:uppercase;
          background:transparent;border:1px solid var(--border);color:var(--text-muted);
          cursor:pointer;border-radius:0;padding:var(--space-2);
        "
      >SPÄTER (ZUR ÜBERSICHT)</button>
    </div>
  `;
}

function showBanner(items: DebtSummaryItem[]): void {
  // Doppelten Einbau verhindern
  if (document.getElementById('laundry-debt-overlay')) return;

  const overlay = document.createElement('div');
  overlay.id = 'laundry-debt-overlay';
  overlay.className = 'modal-backdrop';
  overlay.innerHTML = `<div class="modal" id="laundry-debt-modal" role="dialog" aria-modal="true" aria-label="Immobilien-Abrechnung">${buildBannerHTML(items)}</div>`;
  document.body.appendChild(overlay);

  // Button-Handler
  document.getElementById('debt-banner-pay-btn')?.addEventListener('click', () => {
    closeBanner();
    navigate('/immobilien');
  });

  document.getElementById('debt-banner-later-btn')?.addEventListener('click', () => {
    closeBanner();
    navigate('/immobilien');
  });

  // ESC schließt Banner
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      closeBanner();
      document.removeEventListener('keydown', onKeyDown);
    }
  };
  document.addEventListener('keydown', onKeyDown);
}

/**
 * Laundry-Schulden-Check beim App-Start.
 * Nur aufrufen wenn Spieler eingeloggt ist.
 * Zeigt Banner einmal pro Browser-Session wenn Schulden vorhanden.
 */
export async function checkLaundryDebtOnLogin(): Promise<void> {
  // Nicht eingeloggt → nichts tun
  if (!gameState.isAuthenticated()) return;

  // Bereits in dieser Session gezeigt → nicht erneut zeigen
  if (sessionStorage.getItem(SESSION_KEY)) return;

  try {
    const result = await api.get<{
      businesses: unknown[];
      debtSummary: DebtSummaryItem[];
    }>('/laundry/businesses');

    const { debtSummary } = result;

    // Nur Betriebe mit aktivem Problem (nicht ACTIVE)
    const problematic = debtSummary.filter((d) => d.status !== 'ACTIVE');

    if (problematic.length === 0) {
      // Keine Schulden-Probleme → Session-Marker setzen (keine unnötigen API-Calls)
      sessionStorage.setItem(SESSION_KEY, '1');
      return;
    }

    showBanner(problematic);
  } catch {
    // API-Fehler still ignorieren – Banner nicht kritisch für App-Funktion
  }
}
